import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin } from 'rxjs';
import { ContactForm } from 'src/app/classes/form-groups/contact-form';
import { ExemptVendorForm } from 'src/app/classes/form-groups/exempt-vendor-form';
import {
  CompanyVendor,
  ExemptVendor,
  ExemptVendorType
} from 'src/app/classes/models';
import { CACompanyType } from 'src/app/interfaces/ca-company-type';
import { USCompanyType } from 'src/app/interfaces/us-company-type';
import { ConfirmModalComponent } from 'src/app/modals/confirm-modal/confirm-modal.component';
import { CompanyService } from 'src/app/services/company.service';
import { ExemptVendorService } from 'src/app/services/exempt-vendor.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { vendorNameLengthModalInputs } from 'src/app/validators/vendor-name-length-validator';

@Component({
  selector: 'app-add-edit-exempt-vendor-modal',
  templateUrl: './add-edit-exempt-vendor-modal.component.html',
  styleUrl: './add-edit-exempt-vendor-modal.component.less',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AddEditExemptVendorModalComponent {
  constructor(
    private activeModal: NgbActiveModal,
    private ToastService: ToastService,
    private UtilityService: UtilityService,
    private ExemptVendorService: ExemptVendorService,
    private CompanyService: CompanyService,
    private ModalService: NgbModal
  ) {
    this.ExemptVendorForm.addControl(
      'taxStructure',
      new FormControl<USCompanyType | CACompanyType | null>(null)
    );
  }

  @Input() ExemptVendor?: ExemptVendor;
  @Input() CompanyVendor?: CompanyVendor;
  @Input() CompanyId?: number;

  ExemptVendorForm: ExemptVendorForm = new ExemptVendorForm();

  einMask = this.UtilityService.einMask;
  ssnMask = this.UtilityService.ssnMask;
  phoneMask = this.UtilityService.phoneMask;

  ExemptVendorTypes: ExemptVendorType[] = [];

  usTaxStructures: USCompanyType[] = [
    'C Corporation',
    'S Corporation',
    'Limited Liability Company (LLC)',
    'Partnership',
    'Sole Proprietorship',
    'Trust'
  ];

  caTaxStructures: CACompanyType[] = [
    'Sole Proprietorship',
    'Partnership',
    'Corporation'
  ];

  llcTypes: {
    label: string;
    value: string;
  }[] = [
    { label: 'Partnership', value: 'P' },
    { label: 'C-Corp', value: 'C' },
    { label: 'S-Corp', value: 'S' }
  ];

  countries: Map<string, string> = new Map([
    ['United States', 'US'],
    ['Canada', 'CA']
  ]);

  close(reason?: string) {
    this.activeModal.dismiss(reason);
  }

  save() {
    const errors = this.UtilityService.generateValidationErrorsObject(
      this.ExemptVendorForm
    );

    if (errors) {
      throw this.ToastService.addErrorToast(
        'Error',
        'There is an error in the form below.',
        { returnError: true }
      );
    }

    // Only fields the admin changed: a row mapped from the client's AP system
    // keeps the AP names, which the server accepts at any length.
    const { legalName, dba } = this.ExemptVendorForm.controls;
    const nameLengthModalInputs = vendorNameLengthModalInputs({
      legalName: legalName.dirty ? legalName.value : undefined,
      dba: dba.dirty ? dba.value : undefined
    });
    if (nameLengthModalInputs) {
      const { componentInstance } = this.ModalService.open(
        ConfirmModalComponent,
        { centered: true }
      );
      Object.assign(componentInstance, nameLengthModalInputs);
      return;
    }

    this.activeModal.close();
  }

  ngOnInit(): void {
    if (!this.ExemptVendor) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing ExemptVendor record.',
        { returnError: true }
      );
    }

    if (!this.CompanyId) {
      throw this.ToastService.addErrorToast('Error', 'Missing Company ID.', {
        returnError: true
      });
    }

    forkJoin([
      this.ExemptVendorService.getExemptVendorTypes(
        this.UtilityService.stringifyObjectsInQueryObject({
          CompanyId: this.CompanyId
        })
      )
    ]).subscribe({
      next: ([exemptVendorTypes]) => {
        this.ExemptVendorTypes = exemptVendorTypes;

        this.ExemptVendorForm.patchValue(this.ExemptVendor!);

        const { Contacts } = this.ExemptVendor!;

        if (Contacts && Contacts.length > 0) {
          Contacts.forEach((contact) => {
            const contactForm = new ContactForm();
            contactForm.patchValue(contact);

            ['email', 'phone1'].forEach((controlName) => {
              const control = contactForm.get(controlName);

              if (control) {
                control.removeValidators([Validators.required]);
                control.updateValueAndValidity();
              }
            });

            this.ExemptVendorForm.controls.Contacts.push(contactForm);
          });
        }

        this.ExemptVendorForm.valueChanges.subscribe({
          next: (changes) => {
            Object.assign(this.ExemptVendor!, changes);
          }
        });
      }
    });
  }
}
